import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Formik, Form, FieldArray } from "formik";
import * as Yup from "yup";
import { motion } from "framer-motion";
import { Button, Row, Col, Form as BootstrapForm } from "react-bootstrap";
import toast from "react-hot-toast";
import { CloudUpload } from "@mui/icons-material";

import InputField from "../InputField/InputField";
import SelectField from "../SelectField/SelectField";
import ImageUpload from "../ImageUpload/ImageUpload";
import SEOFields from "../SEOForm/SEOForm";

import {
    getAdminActiveCategories,
    getAdminActiveSubCategories
} from "../../../../actions/categoryActions";
import { getAdminActiveBrands } from "../../../../actions/brandActions";
import { getAdminProduct, createNewProduct, updateProduct } from "../../../../actions/productActions";
import { clearError, clearProductCreated, clearProductUpdated } from "../../../../slices/productSlice";

const ProductForm = () => {
    const { id: productId } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { brands = [] } = useSelector(state => state.brandsState);
    const { subCategories = [], categories = [] } = useSelector(state => state.categoriesState);
    const { product, isProductCreated, isProductUpdated, error } = useSelector(state => state.productState);
    const [removedMainImage, setRemovedMainImage] = useState(false);
    const [removedGalleryImages, setRemovedGalleryImages] = useState([]);
    const [removedVariantImages, setRemovedVariantImages] = useState({});

    const [initialValues, setInitialValues] = useState({
        name: "",
        brand: "",
        category: "",
        subCategory: "",
        price: "",
        stock: "",
        deliveryDays: "",
        variants: [],
        description: [""],
        specifications: [{ key: "", value: "" }],
        mainImage: null,
        gallery: [],
        faq: [{ question: "", answer: "" }],
        seo: { metaTitle: "", metaDescription: "", metaKeywords: "", canonicalUrl: "" },
    });

    useEffect(() => {
        dispatch(getAdminActiveBrands());
        dispatch(getAdminActiveCategories());
    }, [dispatch]);

    useEffect(() => {
        if (productId) dispatch(getAdminProduct(productId));
    }, [dispatch, productId]);

    useEffect(() => {
        if (productId && product?.slug === productId) {
            setInitialValues({
                name: product.productName || "",
                brand: product.brand || "",
                category: product.category || "",
                subCategory: product.subCategory || "",
                price: product.price || "",
                stock: product.stock || "",
                deliveryDays: product.deliveryDays || "",
                variants: product.variants?.map(v => ({
                    ...v,
                    images: v.images || []
                })) || [],
                description: product.description || [""],
                specifications: product.specifications || [{ key: "", value: "" }],
                mainImage: product.mainImage,
                gallery: product.gallery || [],
                faq: product.faq || [{ question: "", answer: "" }],
                seo: product.seo || { metaTitle: "", metaDescription: "", metaKeywords: "", canonicalUrl: "" },
            });
        }
    }, [product, productId]);

    useEffect(() => {
        if (initialValues.category) {
            dispatch(getAdminActiveSubCategories(initialValues.category));
        }
    }, [initialValues.category]);

    useEffect(() => {
        if (isProductCreated) {
            toast.success("Product added successfully!");
            dispatch(clearProductCreated());
            navigate("/admin/products");
        }
        if (isProductUpdated) {
            toast.success("Product updated successfully!");
            dispatch(clearProductUpdated());
            navigate("/admin/products");
        }
        if (error) {
            toast.error(error);
            dispatch(clearError());
        }
    }, [isProductCreated, isProductUpdated, error, dispatch, navigate]);

    const ProductSchema = Yup.object().shape({
        name: Yup.string().required("Required"),
        brand: Yup.string().required("Required"),
        category: Yup.string().required("Required"),
        subCategory: Yup.string().required("Required"),
        price: Yup.number().required("Required"),
        stock: Yup.number().required("Required"),
        mainImage: productId
            ? Yup.mixed().nullable()
            : Yup.mixed().required("Main image is required"),
        gallery: Yup.array()
            .of(Yup.mixed())
            .min(1, "At least one gallery image is required"),
        description: Yup.array().of(Yup.string().required("Description required")).min(1),
        variants: Yup.array().of(
            Yup.object().shape({
                color: Yup.string().required("Color required"),
                size: Yup.string().required("Size required"),
                price: Yup.number().required("Price required"),
                stock: Yup.number().required("Stock required"),
                images: Yup.array().of(Yup.mixed()).min(1, "At least one image is required")
            })
        ),
        specifications: Yup.array().of(
            Yup.object().shape({
                key: Yup.string().required("Key required"),
                value: Yup.string().required("Value required")
            })
        ).min(1),
    });

    const handleSubmit = (values) => {
       const formData = new FormData();

        // Map name to productName
        formData.append("productName", values.name);

        // Other simple fields
        formData.append("brand", values.brand);
        formData.append("category", values.category);
        formData.append("subCategory", values.subCategory);
        formData.append("price", values.price);
        formData.append("stock", values.stock);
        formData.append("deliveryDays", values.deliveryDays);

        // Append JSON fields
        formData.append("description", JSON.stringify(values.description || []));
        formData.append("specifications", JSON.stringify(values.specifications || []));
        formData.append("faq", JSON.stringify(values.faq || []));
        formData.append("seo", JSON.stringify(values.seo || {}));

        // Append main image
        if (values.mainImage) {
            formData.append("mainImage", values.mainImage);
        }

        // Append gallery images
        if (values.gallery && values.gallery.length > 0) {
            values.gallery.forEach(file => formData.append("gallery", file));
        }

        // Append variants
        if (values.variants && values.variants.length > 0) {
            formData.append("variants", JSON.stringify(values.variants.map(v => ({
                color: v.color,
                size: v.size,
                price: v.price,
                stock: v.stock
            }))));

            // Append images separately
            values.variants.forEach((variant, idx) => {
                if (variant.images && variant.images.length > 0) {
                    variant.images.forEach(file => {
                        formData.append(`variants_${idx}_images`, file);
                    });
                }
            });
        }

        formData.append("removedMainImage", removedMainImage);
        formData.append("removedGalleryImages", JSON.stringify(removedGalleryImages));
        formData.append("removedVariantImages", JSON.stringify(removedVariantImages));
         if (productId) {
            dispatch(updateProduct(productId, formData));
        } else {
            dispatch(createNewProduct(formData));
        }
    };

    return (
        <motion.div className="container py-4" initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }}>
            <h4 className="fw-bold mb-4 text-center">{productId ? "Edit Product" : "Add Product"}</h4>
            <Formik
                enableReinitialize
                initialValues={initialValues}
                validationSchema={ProductSchema}
                onSubmit={handleSubmit}
            >
                {({ setFieldValue, values }) => {
                    // Handle brand change
                    const handleBrandChange = (brandId) => {
                        setFieldValue("brand", brandId);
                        setFieldValue("category", "");
                        setFieldValue("subCategory", "");
                    };

                    // Handle category change
                    const handleCategoryChange = (categoryId) => {
                        setFieldValue("category", categoryId);
                        setFieldValue("subCategory", "");
                        if (categoryId) dispatch(getAdminActiveSubCategories(categoryId));
                    };

                    const handleSubCategoryChange = (subCategoryId) => {
                      setFieldValue("subCategory", subCategoryId);
                    };

                    return (
                        <Form>
                            <Row >
                                <Col md={12}>
                                    <InputField label="Product Name *" name="name" type="text" placeholder="Enter product name" />
                                </Col>
                                <Col md={4}>
                                    <SelectField
                                        label="Brand *"
                                        name="brand"
                                        options={brands}
                                        onChange={handleBrandChange}
                                    />
                                </Col>
                                <Col md={4}>
                                    <SelectField
                                        label="Category *"
                                        name="category"
                                        options={categories}
                                        onChange={handleCategoryChange}
                                    />
                                </Col>
                                <Col md={4}>
                                    <SelectField
                                        label="SubCategory *"
                                        name="subCategory"
                                        options={subCategories}
                                        onChange={handleSubCategoryChange}
                                    />
                                </Col>
                                <Col md={4}>
                                    <InputField label="Price *" name="price" type="number" placeholder="Enter product price" />
                                </Col>
                                <Col md={4}>
                                    <InputField label="Stock *" name="stock" type="number" placeholder="Enter product stock" />
                                </Col>
                                <Col md={4}>
                                    <InputField label="Delivery Days" name="deliveryDays" type="number" placeholder="Enter delivery days" />
                                </Col>
                                <Col md={6}>
                                    <ImageUpload
                                        label="Main Image *"
                                        name="mainImage"
                                        setFieldValue={setFieldValue}
                                        multiple={false}
                                        initialFiles={values.mainImage ? [values.mainImage] : []}
                                        onRemove={(removedFile) => {
                                            if (removedFile.keepOld) setRemovedMainImage(true);
                                        }}
                                    />

                                </Col>
                                <Col md={6}>
                                    <ImageUpload
                                        label="Gallery Images *"
                                        name="gallery"
                                        setFieldValue={setFieldValue}
                                        multiple={true}
                                        initialFiles={values.gallery || []}
                                        onRemove={(removedFile) => {
                                            if (removedFile.keepOld)
                                                setRemovedGalleryImages(prev => [...prev, removedFile.url]);
                                        }}
                                    />

                                </Col>
                                <Col md={12}>
                                    <BootstrapForm.Label>Variants</BootstrapForm.Label>
                                    <FieldArray name="variants">
                                        {({ push, remove }) => (
                                            <div>
                                                {(values.variants || []).map((variant, idx) => (
                                                    <div
                                                        key={idx}
                                                        className="mb-3 pb-2 border-bottom"
                                                    >
                                                        <Row className="g-2">
                                                            <Col xs={12} md={3}>
                                                                <InputField label="Color *" name={`variants[${idx}].color`} type="text" placeholder="Enter color" />
                                                            </Col>
                                                            <Col xs={12} md={3}>
                                                                <InputField label="Size *" name={`variants[${idx}].size`} type="text" placeholder="Enter size" />
                                                            </Col>
                                                            <Col xs={12} md={3}>
                                                                <InputField label="Price *" name={`variants[${idx}].price`} type="number" placeholder="Enter price" />
                                                            </Col>
                                                            <Col xs={12} md={3}>
                                                                <InputField label="Stock *" name={`variants[${idx}].stock`} type="number" placeholder="Enter stock" />
                                                            </Col>
                                                            <Col xs={12} md={12}>
                                                                <ImageUpload
                                                                    key={idx}
                                                                    label="Images *"
                                                                    name={`variants[${idx}].images`}
                                                                    setFieldValue={setFieldValue}
                                                                    multiple={true}
                                                                    initialFiles={variant.images || []}
                                                                    onRemove={(removedFile) => {
                                                                        if (removedFile.keepOld) {
                                                                            setRemovedVariantImages(prev => {
                                                                                const updated = { ...prev };
                                                                                if (!updated[idx]) updated[idx] = [];
                                                                                updated[idx].push(removedFile.url);
                                                                                return updated;
                                                                            });
                                                                        }
                                                                    }}
                                                                />
                                                            </Col>
                                                            <div className="mt-2 d-flex justify-content-end">
                                                                <Button variant="danger" size="sm" onClick={() => remove(idx)}>
                                                                    Remove Variant
                                                                </Button>
                                                            </div>

                                                        </Row>
                                                    </div>
                                                ))}

                                                <Button
                                                    variant="success"
                                                    size="sm"
                                                    onClick={() =>
                                                        push({ color: "", size: "", price: "", stock: "", images: [] })
                                                    }
                                                >
                                                    Add Variant
                                                </Button>
                                            </div>
                                        )}
                                    </FieldArray>

                                </Col>
                                <Col md={12} className="mt-2">
                                    <BootstrapForm.Label>Descriptions *</BootstrapForm.Label>
                                    <FieldArray name="description">
                                        {({ push, remove }) => (
                                            <div>
                                                {(values.description || []).map((desc, idx) => (
                                                    <Row key={idx} className="align-items-center mb-2">
                                                        <Col xs={11}>
                                                            <InputField
                                                                label="no"
                                                                name={`description[${idx}]`}
                                                                type="text"
                                                                placeholder={`Enter description ${idx + 1}`}
                                                            />
                                                        </Col>
                                                        <Col xs={1} className="d-flex align-items-end">
                                                            {values.description.length > 1 && (
                                                                <Button
                                                                    variant="danger"
                                                                    size="sm"
                                                                    onClick={() => remove(idx)}
                                                                >
                                                                    X
                                                                </Button>
                                                            )}
                                                        </Col>
                                                    </Row>
                                                ))}

                                                <Button
                                                    variant="success"
                                                    size="sm"
                                                    onClick={() => push("")}
                                                >
                                                    Add Description
                                                </Button>
                                            </div>
                                        )}
                                    </FieldArray>

                                </Col>
                                <Col md={12} className="mt-2">
                                    <BootstrapForm.Label>Specification *</BootstrapForm.Label>
                                    <FieldArray name="specifications">
                                        {({ push, remove }) => (
                                            <div>
                                                {values.specifications?.map((spec, idx) => (
                                                    <div key={idx} className="mb-3">
                                                        <div className="d-flex align-items-start">
                                                            <div className="flex-grow-1 me-2">
                                                                <InputField label="no" name={`specifications[${idx}].key`} type="text" placeholder={`Enter key ${idx + 1}`} />
                                                            </div>
                                                            <div className="flex-grow-1 me-2">
                                                                <InputField label="no" name={`specifications[${idx}].value`} type="text" placeholder={`Enter value ${idx + 1}`} />
                                                            </div>
                                                            <div>
                                                                <Button
                                                                    variant="danger"
                                                                    size="sm"
                                                                    onClick={() => remove(idx)}
                                                                >
                                                                    X
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}

                                                <Button
                                                    variant="success"
                                                    size="sm"
                                                    onClick={() => push({ key: "", value: "" })}
                                                >
                                                    Add Specification
                                                </Button>
                                            </div>
                                        )}
                                    </FieldArray>
                                </Col>
                                {/* <Col md={12} className="mt-2">
                                    <BootstrapForm.Label>FAQ</BootstrapForm.Label>
                                    <FieldArray name="faq">
                                        {({ push, remove }) => (
                                            <div>
                                                {values.faq?.map((item, idx) => (
                                                    <div key={idx} className="mb-3 pb-2 border-bottom">
                                                       <Row className="mb-2 align-items-center">
                                                            <Col xs={12} md={12}>
                                                                <InputField label="Qtn" name={`faq[${idx}].question`} type="text" placeholder={`Enter question ${idx + 1}`} />
                                                            </Col>
                                                        </Row>

                                                        <Row>
                                                            <Col xs={12}>
                                                                <InputField label="Ans" name={`faq[${idx}].answer`} type="textarea" placeholder={`Enter answer ${idx + 1}`} />
                                                            </Col>
                                                        </Row>
                                                        <div className="mt-2 d-flex justify-content-end">
                                                            <Button variant="danger" size="sm" onClick={() => remove(idx)}>
                                                                Remove FAQ
                                                            </Button>
                                                        </div>
                                                    </div>
                                                ))}

                                                <Button
                                                    variant="success"
                                                    size="sm"
                                                    onClick={() => push({ question: "", answer: "" })}
                                                >
                                                    Add FAQ
                                                </Button>
                                            </div>
                                        )}
                                    </FieldArray>
                                </Col> */}
                                <SEOFields values={values.seo} setFieldValue={setFieldValue} />
                                <Col md={12} className="text-center mt-4">
                                    <motion.div whileHover={{ scale: 1.05 }}>
                                        <Button type="submit" variant="success" className="px-5 py-2 fw-bold">
                                            <CloudUpload className="me-2" />
                                            {productId ? "Update Product" : "Save Product"}
                                        </Button>
                                    </motion.div>
                                </Col>

                            </Row>
                        </Form>
                    )
                }}
            </Formik>
        </motion.div>
    );
};

export default ProductForm;
